import {
  Home,
  Images,
  Brain,
  ListOrdered,
  FileText,
  Heart,
  CheckSquare,
  Target,
  ListTodo,
  Users,
  Calendar,
  StickyNote,
  CalendarDays,
  ListCheck,
  Bot,
  Play,
  MessageSquare,
  FlaskConical,
  Wand2,
  Clock,
  Eye,
  BarChart3,
  BarChart2,
  Trophy,
  Settings,
  Lightbulb,
  Sparkles,
  Smile,
  UserCog,
  Contact,
  BookUser,
  BookOpen,
  Zap,
  Award,
  Key,
  Layers,
  Mic,
  Film,
  HardDrive,
  CreditCard,
  HeartPulse,
  UtensilsCrossed,
  Dumbbell,
  Activity,
  Salad,
  Shield,
  ShieldCheck,
  Building2,
} from "lucide-react";

export interface MenuItemDefinition {
  id: string;
  name: string;
  icon: any;
  href: string;
  parentId?: string;
  isSection?: boolean;
  isCollapsible?: boolean;
  isStaticChild?: boolean;
  currentMatch?: (location: string) => boolean;
}

export const menuItemFeatureKeyOverrides: Record<string, string> = {
  coach: "assistants",
  "assistants-sops": "assistants",
};

export type AssistantCapability =
  | "notes" | "goals" | "calendar" | "tasks" | "notepad" | "habits" | "trackerX"
  | "contacts" | "ai" | "mcp" | "retellInbound" | "retellOutbound" | "vault" | "media"
  | "planner" | "intentions" | "gratefuls" | "incantations" | "checklists"
  | "drive" | "meditation" | "analytics" | "vboard" | "sops" | "timeBudget" | "suggestions";

/** Capability required for an assistant to see a navigation item. Undefined
 * means the item is not delegated to assistants (and is therefore hidden). */
export const menuItemCapabilityMap: Partial<Record<string, AssistantCapability>> = {
  "my-goals": "goals", "goals": "goals", habits: "habits", tasks: "tasks",
  trackerx: "trackerX", calendar: "calendar", notepad: "notepad", contacts: "contacts",
  journal: "notes", zeus: "ai", vault: "vault", "api-keys": "mcp",
  "ai-coach": "ai", "ai-coach-start": "ai", "ai-coach-ask": "ai",
  "goal-simulation": "ai", "ai-create": "ai",
  // Extended capabilities
  "time-budget": "timeBudget",
  "media-library": "media",
  planner: "planner",
  intentions: "intentions",
  gratefuls: "gratefuls",
  incantations: "incantations",
  checklists: "checklists",
  "google-drive": "drive",
  meditation: "meditation",
  analytics: "analytics",
  vboard: "vboard",
  sops: "sops",
  suggestions: "suggestions",
};

export function assistantCanAccessMenuItem(
  id: string,
  permissions: Partial<Record<AssistantCapability, boolean>> | undefined,
): boolean {
  const capability = menuItemCapabilityMap[id];
  return !!capability && permissions?.[capability] === true;
}

const PATH_PREFIX_CAPABILITIES: Array<[string, AssistantCapability]> = [
  ["/goals", "goals"], ["/tasks", "tasks"], ["/habits", "habits"],
  ["/trackerx", "trackerX"], ["/calendar", "calendar"], ["/notepad", "notepad"],
  ["/journal", "notes"], ["/contacts", "contacts"], ["/vault", "vault"],
  ["/api-keys", "mcp"], ["/zeus", "ai"], ["/ai-coach", "ai"],
  ["/goal-simulation", "ai"], ["/ai-create", "ai"],
  // Extended capabilities
  ["/time-budget", "timeBudget"],
  ["/media", "media"],
  ["/planner", "planner"],
  ["/intentions", "intentions"],
  ["/gratefuls", "gratefuls"],
  ["/incantations", "incantations"],
  ["/checklists", "checklists"],
  ["/drive", "drive"],
  ["/meditation", "meditation"],
  ["/analytics", "analytics"],
  ["/vboard", "vboard"],
  ["/suggestions", "suggestions"],
];
export function assistantCanAccessPath(
  path: string,
  permissions: Partial<Record<AssistantCapability, boolean>> | undefined,
): boolean {
  // "/" (dashboard) is always accessible.
  if (path === "/") return true;
  // SOPs are read-only content pages; the backend enforces per-user visibility
  // (audience, status, and per-assistant share grants). All active assistants
  // may browse /sops without an additional capability grant.
  if (path === "/sops" || path.startsWith("/sops/")) return true;

  const directCapability = PATH_PREFIX_CAPABILITIES.find(([prefix]) =>
    path === prefix || path.startsWith(`${prefix}/`)
  )?.[1];
  if (directCapability) return permissions?.[directCapability] === true;
  const item = menuItemDefinitions.find((candidate) =>
    candidate.href !== "#" && (candidate.currentMatch?.(path) || candidate.href === path),
  );
  return item ? assistantCanAccessMenuItem(item.id, permissions) : false;
}
export const menuItemDefinitions: MenuItemDefinition[] = [
  { id: "dashboard", name: "Dashboard", icon: Home, href: "/", currentMatch: (loc) => loc === "/" },
  { id: "zeus", name: "Zeus", icon: Zap, href: "/zeus", currentMatch: (loc) => loc === "/zeus" },
  { id: "communicate", name: "Communicate", icon: MessageSquare, href: "/communicate", currentMatch: (loc) => loc === "/communicate" || loc.startsWith("/communicate") },

  { id: "mind", name: "Mind", icon: Zap, href: "#", isSection: true, isCollapsible: true },
  { id: "intentions", name: "Affirmations", icon: ListOrdered, href: "/intentions", parentId: "mind", currentMatch: (loc) => loc === "/intentions" },
  { id: "journal", name: "Journal", icon: FileText, href: "/journal", parentId: "mind", currentMatch: (loc) => loc === "/journal" || loc === "/journal/analytics" || loc === "/journal/search" },
  { id: "gratefuls", name: "Gratefuls", icon: Heart, href: "/gratefuls", parentId: "mind", currentMatch: (loc) => loc === "/gratefuls" },
  { id: "incantations", name: "Identity", icon: Mic, href: "/incantations", parentId: "mind", currentMatch: (loc) => loc === "/incantations" },
  { id: "meditation", name: "MnM", icon: Brain, href: "/meditation", parentId: "mind", currentMatch: (loc) => loc === "/meditation" || loc === "/meditation/settings" },

  { id: "achieve", name: "Achieve", icon: Award, href: "#", isSection: true, isCollapsible: true },
  { id: "habits", name: "Habits", icon: CheckSquare, href: "/habits", parentId: "achieve", currentMatch: (loc) => loc === "/habits" },
  { id: "my-goals", name: "My Goals", icon: Target, href: "/goals", parentId: "achieve", currentMatch: (loc) => loc === "/goals" },
  { id: "vboard", name: "Vboard", icon: Images, href: "/vboard", parentId: "achieve", currentMatch: (loc) => loc === "/vboard" },
  { id: "tasks", name: "Projects-Tasks", icon: ListTodo, href: "/tasks", parentId: "achieve", currentMatch: (loc) => loc === "/tasks" },
  { id: "trackerx", name: "TrackerX", icon: BarChart2, href: "/trackerx", parentId: "achieve", currentMatch: (loc) => loc === "/trackerx" },

  { id: "media", name: "Media", icon: Film, href: "#", isSection: true, isCollapsible: true },
  { id: "media-library", name: "Media Library", icon: Film, href: "/media", parentId: "media", currentMatch: (loc) => loc === "/media" },
  { id: "google-drive", name: "Google Drive", icon: HardDrive, href: "/drive", parentId: "media", currentMatch: (loc) => loc === "/drive" },

  { id: "organize", name: "Organize", icon: Layers, href: "#", isSection: true, isCollapsible: true },
  { id: "calendar", name: "Calendar", icon: Calendar, href: "/calendar", parentId: "organize", currentMatch: (loc) => loc === "/calendar" },
  { id: "notepad", name: "Notepad", icon: StickyNote, href: "/notepad", parentId: "organize", currentMatch: (loc) => loc === "/notepad" },
  { id: "planner", name: "Planner", icon: CalendarDays, href: "/planner", parentId: "organize", currentMatch: (loc) => loc === "/planner" },
  { id: "checklists", name: "Checklists", icon: ListCheck, href: "/checklists", parentId: "organize", currentMatch: (loc) => loc === "/checklists" || loc === "/checklists/completed" },
  { id: "health", name: "Health", icon: HeartPulse, href: "#", parentId: "organize", isCollapsible: true, currentMatch: (loc) => loc === "/meal-planner" || loc === "/workout-planner" || loc === "/health-metrics" || loc === "/food-goal" || loc === "/fitness-goal" },
  { id: "meal-planner", name: "Meal Planner", icon: UtensilsCrossed, href: "/meal-planner", parentId: "health", isStaticChild: true, currentMatch: (loc) => loc === "/meal-planner" },
  { id: "workout-planner", name: "Workout Planner", icon: Dumbbell, href: "/workout-planner", parentId: "health", isStaticChild: true, currentMatch: (loc) => loc === "/workout-planner" },
  { id: "health-metrics", name: "Metrics", icon: Activity, href: "/health-metrics", parentId: "health", isStaticChild: true, currentMatch: (loc) => loc === "/health-metrics" },
  { id: "food-goal", name: "Food Goal", icon: Salad, href: "/food-goal", parentId: "health", isStaticChild: true, currentMatch: (loc) => loc === "/food-goal" },
  { id: "fitness-goal", name: "Fitness Goal", icon: Dumbbell, href: "/fitness-goal", parentId: "health", isStaticChild: true, currentMatch: (loc) => loc === "/fitness-goal" },

  { id: "ai-coach", name: "AI Coach", icon: Bot, href: "#", isSection: true, isCollapsible: true, currentMatch: (loc) => loc.startsWith("/ai-coach") },
  { id: "ai-coach-start", name: "AI Coach", icon: Play, href: "/ai-coach/start", parentId: "ai-coach", isStaticChild: true, currentMatch: (loc) => loc === "/ai-coach/start" || loc === "/ai-coach/settings" || loc.startsWith("/ai-coach/constraints") || loc === "/ai-coach/reporting" },
  { id: "ai-coach-ask", name: "Ask AI", icon: MessageSquare, href: "/ai-coach/ask", parentId: "ai-coach", isStaticChild: true, currentMatch: (loc) => loc === "/ai-coach/ask" },
  { id: "goal-simulation", name: "Goal Simulation", icon: FlaskConical, href: "/goal-simulation", parentId: "ai-coach", currentMatch: (loc) => loc === "/goal-simulation" },
  { id: "ai-create", name: "AI Create", icon: Wand2, href: "/ai-create", parentId: "ai-coach", currentMatch: (loc) => loc === "/ai-create" },
  { id: "time-budget", name: "Time Budget", icon: Clock, href: "/time-budget", parentId: "ai-coach", currentMatch: (loc) => loc === "/time-budget" },

  { id: "review", name: "Review", icon: Eye, href: "#", isSection: true, isCollapsible: true },
  { id: "analytics", name: "Analytics", icon: BarChart3, href: "/analytics", parentId: "review", currentMatch: (loc) => loc === "/analytics" },
  { id: "achievements", name: "Achievements", icon: Trophy, href: "/achievements", parentId: "review", currentMatch: (loc) => loc === "/achievements" },
  { id: "scorecard", name: "Scorecard", icon: Trophy, href: "/scorecard", parentId: "review", currentMatch: (loc) => loc === "/scorecard" },
  { id: "settings", name: "Settings", icon: Settings, href: "#", isSection: true, isCollapsible: true, currentMatch: (loc) => loc === "/settings" || loc === "/zeus-settings" || loc === "/suggestions" || loc === "/tours" || loc.startsWith("/tour/") || loc === "/testimonials" || loc === "/api-keys" || loc === "/billing" || loc === "/vault" },
  { id: "settings-main", name: "Preferences", icon: Settings, href: "/settings", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/settings" },
  { id: "sops", name: "SOPs", icon: BookOpen, href: "/sops", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/sops" || loc.startsWith("/sops/") },
  { id: "zeus-settings", name: "Zeus Settings", icon: Zap, href: "/zeus-settings", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/zeus-settings" },
  { id: "vault", name: "Vault", icon: ShieldCheck, href: "/vault", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/vault" },
  { id: "billing", name: "Billing", icon: CreditCard, href: "/billing", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/billing" },
  { id: "api-keys", name: "API Keys", icon: Key, href: "/api-keys", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/api-keys" },
  { id: "suggestions", name: "Suggestions", icon: Lightbulb, href: "/suggestions", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/suggestions" },
  { id: "tours", name: "Tours", icon: Sparkles, href: "/tours", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/tours" || loc.startsWith("/tour/") },
  { id: "testimonials", name: "Testimonials", icon: Smile, href: "/testimonials", parentId: "settings", isStaticChild: true, currentMatch: (loc) => loc === "/testimonials" },

  { id: "crm", name: "CRM", icon: Contact, href: "#", isSection: true, isCollapsible: true },
  { id: "contacts", name: "Contacts", icon: BookUser, href: "/contacts", parentId: "crm", currentMatch: (loc) => loc === "/contacts" },
  { id: "assistants", name: "Assistants", icon: UserCog, href: "/assistants", parentId: "crm", currentMatch: (loc) => loc === "/assistants" || loc === "/time-reports" },
  { id: "coach", name: "Coach", icon: Users, href: "/coach", parentId: "crm", currentMatch: (loc) => loc === "/coach" },
  { id: "assistants-sops", name: "SOPs & FAQs", icon: BookOpen, href: "/assistants/sops", parentId: "crm", currentMatch: (loc) => loc === "/assistants/sops" },

  { id: "master-admin", name: "Master Admin", icon: Shield, href: "#", isSection: true, isCollapsible: true, currentMatch: (loc) => loc === "/master-admin" || loc === "/admin/manage-tenants" || loc === "/manage-tenants" || loc === "/admin/platform-billing" || loc === "/admin/platform-roles" },
  { id: "master-admin-home", name: "Overview", icon: Shield, href: "/master-admin", parentId: "master-admin", isStaticChild: true, currentMatch: (loc) => loc === "/master-admin" },
  { id: "platform-operations", name: "Platform Operations", icon: Building2, href: "/admin/manage-tenants", parentId: "master-admin", isStaticChild: true, currentMatch: (loc) => loc === "/admin/manage-tenants" || loc === "/manage-tenants" },
  { id: "platform-billing", name: "Platform Billing", icon: CreditCard, href: "/admin/platform-billing", parentId: "master-admin", isStaticChild: true, currentMatch: (loc) => loc === "/admin/platform-billing" },
  { id: "platform-roles", name: "Platform Roles", icon: ShieldCheck, href: "/admin/platform-roles", parentId: "master-admin", isStaticChild: true, currentMatch: (loc) => loc === "/admin/platform-roles" },
];

export function getMenuItemsByParent(parentId: string): MenuItemDefinition[] {
  return menuItemDefinitions.filter(item => item.parentId === parentId);
}

export function getSections(): MenuItemDefinition[] {
  return menuItemDefinitions.filter(item => item.isSection);
}

export function getTopLevelItems(): MenuItemDefinition[] {
  return menuItemDefinitions.filter(item => !item.parentId);
}

export function getAllMenuItemIds(): string[] {
  return menuItemDefinitions.map(item => item.id);
}

const CAPABILITY_DISPLAY_NAMES: Record<AssistantCapability, string> = {
  goals: "Goals",
  tasks: "Tasks",
  habits: "Habits",
  trackerX: "TrackerX",
  calendar: "Calendar",
  notepad: "Notepad",
  notes: "Journal",
  contacts: "Contacts",
  vault: "Vault",
  mcp: "API Keys",
  ai: "AI features",
  retellInbound: "Retell Inbound",
  retellOutbound: "Retell Outbound",
  media: "Media Library",
  planner: "Daily Planner",
  intentions: "Intentions",
  gratefuls: "Gratitude",
  incantations: "Incantations",
  checklists: "Checklists",
  drive: "Google Drive",
  meditation: "Meditation",
  analytics: "Analytics",
  vboard: "Vision Board",
  sops: "SOPs",
  timeBudget: "Time Budget",
  suggestions: "Suggestions",
};

/** Returns a human-readable section name for the capability required by the
 *  given path, or undefined if no specific capability can be identified. */
export function getRequiredSectionNameForPath(path: string): string | undefined {
  const capability = PATH_PREFIX_CAPABILITIES.find(([prefix]) =>
    path === prefix || path.startsWith(`${prefix}/`)
  )?.[1];
  if (capability) return CAPABILITY_DISPLAY_NAMES[capability];
  const item = menuItemDefinitions.find((candidate) =>
    candidate.href !== "#" && (candidate.currentMatch?.(path) || candidate.href === path),
  );
  if (item) {
    const itemCapability = menuItemCapabilityMap[item.id];
    if (itemCapability) return CAPABILITY_DISPLAY_NAMES[itemCapability];
  }
  return undefined;
}
